import { getDatabase, ref, update, get } from 'firebase/database';
import { getUserPlan, SUBSCRIPTION_PLANS } from './stripe';

// Track daily uploads for users
export const trackUserUpload = async (userId) => {
  const db = getDatabase();
  const today = new Date().toDateString();
  const userRef = ref(db, `users/${userId}/dailyUploads/${today}`);
  
  try {
    const snapshot = await get(userRef);
    const currentUploads = snapshot.val() || 0;
    
    await update(ref(db, `users/${userId}/dailyUploads`), {
      [today]: currentUploads + 1
    });
    
    return currentUploads + 1;
  } catch (error) {
    console.error('Error tracking upload:', error);
    return 0;
  }
};

// Check if user can upload based on their plan and daily limit
export const canUserUploadToday = async (user) => {
  if (!user) return false;
  
  const userPlan = getUserPlan(user);
  
  // Premium users have unlimited uploads
  if (userPlan.limitations.dailyUploads === -1) {
    return true;
  }
  
  // Check daily upload count for free users
  const db = getDatabase();
  const today = new Date().toDateString();
  const userRef = ref(db, `users/${user.uid}/dailyUploads/${today}`);
  
  try {
    const snapshot = await get(userRef);
    const todayUploads = snapshot.val() || 0;
    
    return todayUploads < userPlan.limitations.dailyUploads;
  } catch (error) {
    console.error('Error checking upload limit:', error);
    return false;
  }
};

// Get user's remaining uploads for today
export const getRemainingUploads = async (user) => {
  if (!user) return 0;
  
  const userPlan = getUserPlan(user);
  
  // Premium users have unlimited uploads
  if (userPlan.limitations.dailyUploads === -1) {
    return -1; // unlimited
  }
  
  const db = getDatabase();
  const today = new Date().toDateString();
  const userRef = ref(db, `users/${user.uid}/dailyUploads/${today}`);
  
  try {
    const snapshot = await get(userRef);
    const todayUploads = snapshot.val() || 0;
    
    return Math.max(0, userPlan.limitations.dailyUploads - todayUploads);
  } catch (error) {
    console.error('Error getting remaining uploads:', error);
    return 0;
  }
};

// Update user subscription status (called by webhook)
export const updateUserSubscription = async (userId, subscriptionData) => {
  const db = getDatabase();
  const userRef = ref(db, `users/${userId}/subscription`);
  
  try {
    await update(userRef, {
      status: subscriptionData.status,
      planId: subscriptionData.planId,
      customerId: subscriptionData.customerId,
      subscriptionId: subscriptionData.subscriptionId,
      currentPeriodStart: subscriptionData.currentPeriodStart,
      currentPeriodEnd: subscriptionData.currentPeriodEnd,
      cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd,
      updatedAt: new Date().toISOString()
    });
    
    console.log('User subscription updated successfully');
  } catch (error) {
    console.error('Error updating user subscription:', error);
    throw error;
  }
};

// Show upgrade prompt for free users
export const shouldShowUpgradePrompt = async (user, action) => {
  if (!user) return false;
  
  const userPlan = getUserPlan(user);
  
  // Don't show prompt to premium users
  if (userPlan.id !== 'free') return false;
  
  switch (action) {
    case 'upload':
      const canUpload = await canUserUploadToday(user);
      return !canUpload;
    
    case 'analytics':
      return !userPlan.limitations.analytics;
    
    case 'superlike':
      // Check if user has used their super likes (implement this based on your needs)
      return true;
    
    default:
      return false;
  }
};

// Format subscription status for display
export const formatSubscriptionStatus = (subscription) => {
  if (!subscription) return 'Free';
  
  switch (subscription.status) {
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Payment Issue';
    case 'canceled':
      return 'Canceled';
    case 'unpaid':
      return 'Payment Failed';
    case 'incomplete':
      return 'Incomplete';
    default:
      return subscription.status;
  }
};

// Check if user has premium features
export const hasPremiumFeatures = (user) => {
  const userPlan = getUserPlan(user);
  return userPlan.id !== 'free';
};

// Get premium feature availability
export const getPremiumFeatures = (user) => {
  const userPlan = getUserPlan(user);
  return userPlan.limitations;
};